import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import ScoreGraph from "../../../Charts/ScoreGraph";
import QuantGraphDetailed from "../../../Charts/QuantGraphDetailed";
import QuantGraphGlobal from "../../../Charts/QuantGraphGlobal";
import StepperButtons from "../../StepperButtons";
import InformationDialogue from "../../../CustomComponents/InformationDialogue";
import { compare } from "../../../../context/designPhase";

export default function GraphTabs({ payload, lastOnly = false }) {
    const styling = useTheme();

    const superThemes = useSelector((state) => state.themes.superthemes);
    const applicationThemes = useSelector((state) => state.themes.themes);
    const flatThemes = useSelector((state) => state.themes.flatThemes);
    const flatSubThemes = useSelector((state) => state.themes.flatSubThemes);
    const flatKpis = useSelector((state) => state.themes.flatKpis);
    const projectThemes = useSelector((state) => state.projects.selectedProject.themes);

    const projectLayers = payload.project.potential_layers.filter((pl) => payload.project.layers.includes(pl.id));
    const projectSegments = payload.phase?.segments || [];

    let designs = payload?.loop?.designs || [];
    if (payload.project.reference_design) {
        designs = [payload.project.reference_design, ...designs];
    }

    const [segmentFilter, setSegmentFilter] = useState(projectSegments.map((segment) => segment.id));
    const showGoalLine = projectSegments.length === segmentFilter.length;

    const handleFilterChange = (event) => {
        setSegmentFilter(event.target.value);
    };

    // Build a flat list of graph items, one per chip.
    const graphItems = useMemo(() => {
        const items = [];

        superThemes.forEach((supertheme) => {
            const relatedThemeIDs = applicationThemes
                .filter((theme) => theme.supertheme === supertheme.id)
                .map((theme) => theme.id);

            const relatedProjectThemes = projectThemes
                .filter((projectTheme) => relatedThemeIDs.includes(projectTheme.theme))
                .filter((projectTheme) => projectTheme.subthemes.some((projectSubtheme) => projectSubtheme.goals.length > 0));

            const hasQualitative = relatedProjectThemes.some((projectTheme) =>
                projectTheme.subthemes.some((projectSubtheme) =>
                    projectSubtheme.goals.some((goal) => flatKpis[goal.kpi]?.scale_type === 0)
                )
            );

            if (hasQualitative) {
                const qualitativeProjectThemes = relatedProjectThemes.filter((projectTheme) =>
                    projectTheme.subthemes.some((projectSubtheme) =>
                        projectSubtheme.goals.some((goal) => flatKpis[goal.kpi]?.scale_type === 0)
                    )
                );
                items.push({
                    id: `scores_${supertheme.id}`,
                    label: `${supertheme.name} — Scores`,
                    description: `Kwalitatieve scores voor ${supertheme.name}`,
                    type: "qualitative",
                    qualitativeProjectThemes,
                });
            }

            const quantativeProjectThemes = relatedProjectThemes.filter((projectTheme) =>
                projectTheme.subthemes.some((projectSubtheme) =>
                    projectSubtheme.goals.some((goal) => flatKpis[goal.kpi]?.scale_type > 0)
                )
            );

            quantativeProjectThemes.forEach((projectTheme) => {
                const theme = flatThemes[projectTheme.theme];
                const scoresByTheme = designs[0]?.scores_by_themes?.[projectTheme.id];
                if (!scoresByTheme) return;

                Object.keys(scoresByTheme).forEach((projectSubThemeID) => {
                    const projectSubTheme = projectTheme.subthemes.find(
                        (subtheme) => subtheme.id.toString() === projectSubThemeID
                    );
                    if (!projectSubTheme) return;
                    const subtheme = flatSubThemes[projectSubTheme.subtheme];

                    Object.values(scoresByTheme[projectSubThemeID]).forEach((scoreGoal, goalIndex) => {
                        const kpi = flatKpis[scoreGoal.kpi];
                        if (!kpi || kpi.scale_type <= 0) return;
                        const projectGoal = projectSubTheme.goals.find((g) => g.kpi === kpi.id);
                        const unitSuffix = kpi.unit ? ` (${kpi.unit})` : "";
                        items.push({
                            id: `goal_${projectTheme.id}_${projectSubTheme.id}_${scoreGoal.kpi}`,
                            label: `${kpi.name}${unitSuffix}`,
                            description: `${theme.name} / ${subtheme.name}`,
                            type: "quantitative",
                            projectThemeID: projectTheme.id.toString(),
                            projectSubThemeID,
                            goalIndex,
                            kpi,
                            projectGoal,
                            subtheme,
                            theme,
                        });
                    });
                });
            });
        });

        return items;
    }, [superThemes, applicationThemes, projectThemes, flatThemes, flatSubThemes, flatKpis, designs]);

    const [selectedGraphId, setSelectedGraphId] = useState(graphItems[0]?.id || null);

    // Fallback if the current selection is no longer available (e.g. data refreshed).
    const currentGraph =
        graphItems.find((g) => g.id === selectedGraphId) || graphItems[0] || null;

    const qualitativeDataConstructor = (designs, qualitativeProjectThemes) => {
        let ix = -1;
        return qualitativeProjectThemes.reduce((acc, projectTheme) => {
            projectTheme.subthemes.reduce((acc, projectSubtheme) => {
                projectSubtheme.goals
                    .filter((goal) => flatKpis[goal.kpi].scale_type === 0)
                    .reduce((acc, goal) => {
                        let name = flatKpis[goal.kpi].name;
                        let value = goal.value;
                        if (name.toUpperCase() === "SCORE") {
                            name = `${flatThemes[projectTheme.theme].name}`;
                        }

                        ix += 1;
                        acc.push({
                            label: name,
                            data: [
                                value,
                                ...designs.map(
                                    (design) =>
                                        design.scores_by_themes[projectTheme.id.toString()][projectSubtheme.id.toString()].find(
                                            (scoreObj) => scoreObj.kpi === goal.kpi
                                        ).value
                                ),
                            ],
                            backgroundColor: Object.values(styling.palette.graphics)[ix],
                        });
                        return acc;
                    }, acc);
                return acc;
            }, acc);
            return acc;
        }, []);
    };

    const detailedScoreDataConstructor = (designs, projectThemeID, projectSubthemeID, goalIndex) => {
        let formattedData = [];
        try {
            formattedData = projectLayers.map((layer, layerIndex) => ({
                label: layer.name,
                data: designs.map((design) =>
                    design.scores_by_themes[projectThemeID][projectSubthemeID][goalIndex]?.value
                        .filter((segment) => segmentFilter.includes(segment.segment) || segment.segment === "total")
                        .reduce(
                            (acc, segment) =>
                                acc +
                                Number(
                                    segment.values
                                        .filter((layerScore) => layerScore.layer === layer.id)
                                        .reduce((acc, layerScore) => acc + layerScore.value, 0)
                                ),
                            0
                        )
                ),
                type: "bar",
                backgroundColor: Object.values(styling.palette.graphics)[layerIndex],
            }));
        } catch (error) {
            console.log(`Showing global score for goal ${goalIndex} of subtheme ${projectSubthemeID}`);

            formattedData = [
                {
                    label: "Totaal",
                    data: designs.map((design) => {
                        let value = "noscores";
                        try {
                            value = design.scores_by_themes[projectThemeID][projectSubthemeID][goalIndex]?.value;
                        } catch (error) {
                            console.log("Ontbrekend score object voor ontwerp:", design.name);
                        }

                        if (value === null) {
                            console.log("Geen data voor ontwerp:", design.name);
                            return 0;
                        } else if (typeof value === "object") {
                            try {
                                return Object.values(value).reduce((acc, val) => acc + val.value, 0);
                            } catch (error) {
                                console.log("data structuur verkeerd voor ontwerp:", design.name);
                                return 0;
                            }
                        } else {
                            return 0;
                        }
                    }),
                    type: "bar",
                },
            ];
        }
        return formattedData;
    };

    const globalScoreDataConstructor = (designs, projectThemeID, projectSubthemeID, goalIndex) => ({
        label: "",
        data: designs.map((design) => design.scores_by_themes[projectThemeID][projectSubthemeID][goalIndex].value),
        type: "bar",
        backgroundColor: Object.values(styling.palette.graphics)[0],
    });

    if (designs.length === 0 || payload.project.phases.length === 0) {
        return (
            <Box>
                <Typography>Er zijn nog geen ontwerpen. Voeg een ontwerp toe om de scores te kunnen bekijken.</Typography>
                {!lastOnly && <StepperButtons payload={payload} />}
            </Box>
        );
    }

    const variantNames = designs.map((design) => design.name);

    return (
        <Box>
            <InformationDialogue text={compare} />

            {/* Chip selector, one chip per graph */}
            <Box sx={{ mb: 3, mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h6">Selecteer grafiek:</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {graphItems.map((item) => (
                        <Tooltip key={item.id} title={item.description || ""} enterDelay={1000}>
                            <Chip
                                label={item.label}
                                onClick={() => setSelectedGraphId(item.id)}
                                color={selectedGraphId === item.id ? "primary" : "default"}
                                variant={selectedGraphId === item.id ? "filled" : "outlined"}
                                sx={{
                                    fontWeight: selectedGraphId === item.id ? "bold" : "normal",
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
            </Box>

            {/* Selected graph */}
            {currentGraph && (
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {currentGraph.label}
                    </Typography>

                    {currentGraph.type === "qualitative" && (
                        <Grid container>
                            <Grid item xs={12} sx={{ maxHeight: 350, minHeight: 250 }}>
                                <ScoreGraph
                                    initData={qualitativeDataConstructor(designs, currentGraph.qualitativeProjectThemes)}
                                    variants={variantNames}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {currentGraph.type === "quantitative" && (
                        <Grid container spacing={1}>
                            {currentGraph.kpi.scale_type === 1 && (
                                <Grid item xs={12}>
                                    <Box>
                                        <FormControl>
                                            <InputLabel id="segment-filter-label">Gebruiksfases</InputLabel>
                                            <Select
                                                autoWidth
                                                multiple
                                                multiline
                                                label={"Gebruiksfases"}
                                                value={segmentFilter}
                                                sx={{ width: "fit-content" }}
                                                onChange={handleFilterChange}
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            flexWrap: segmentFilter.length < 10 ? "no-wrap" : "wrap",
                                                            gap: 0.2,
                                                        }}
                                                    >
                                                        {selected.map((value) => (
                                                            <Chip
                                                                size="small"
                                                                key={value}
                                                                label={projectSegments.find((segment) => segment.id === value)?.name || value}
                                                                color="primary"
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                MenuProps={{
                                                    anchorOrigin: {
                                                        vertical: "top",
                                                        horizontal: "left",
                                                    },
                                                    transformOrigin: {
                                                        vertical: "bottom",
                                                        horizontal: "left",
                                                    },
                                                }}
                                            >
                                                {projectSegments.map((segment) => (
                                                    <MenuItem key={segment.id} value={segment.id}>
                                                        {segment.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            )}

                            <Grid sx={{ maxHeight: 400, minHeight: 200, minWidth: 400 }} item xs={12} xl={6}>
                                {currentGraph.kpi.scale_type === 1 ? (
                                    <QuantGraphDetailed
                                        variants={variantNames}
                                        layers={projectLayers}
                                        segments={projectSegments}
                                        initData={detailedScoreDataConstructor(
                                            designs,
                                            currentGraph.projectThemeID,
                                            currentGraph.projectSubThemeID,
                                            currentGraph.goalIndex
                                        )}
                                        segmentFilter={segmentFilter}
                                        kpi={currentGraph.kpi}
                                        goal={currentGraph.projectGoal}
                                        showGoalLine={showGoalLine}
                                    />
                                ) : (
                                    <QuantGraphGlobal
                                        variants={variantNames}
                                        initData={globalScoreDataConstructor(
                                            designs,
                                            currentGraph.projectThemeID,
                                            currentGraph.projectSubThemeID,
                                            currentGraph.goalIndex
                                        )}
                                        kpi={currentGraph.kpi}
                                        goal={currentGraph.projectGoal}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Box>
            )}

            {!lastOnly && <StepperButtons payload={payload} updateStatus={false} />}
        </Box>
    );
}

GraphTabs.propTypes = {
    payload: PropTypes.object.isRequired,
    lastOnly: PropTypes.bool,
};
